import React, { useState, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useCartStore from '../../context/cartStore';
import { useAuth } from '../../context/AuthContext';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { count, openCart } = useCartStore();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleLang = () => {
    const next = i18n.language === 'ar' ? 'en' : 'ar';
    i18n.changeLanguage(next);
  };

  return (
    <>
      <nav className={`navbar ${scrolled ? 'navbar--scrolled' : ''}`}>
        <div className="container navbar__inner">
          {/* Logo */}
          <Link to="/" className="navbar__logo">
            <img src="/logo.jpg" alt="Qaya KW" height="44" />
          </Link>

          {/* Desktop Nav */}
          <ul className="navbar__links">
            <li><NavLink to="/shop">{t('nav.shop')}</NavLink></li>
            <li><NavLink to="/about">{t('nav.ourStory')}</NavLink></li>
            <li><NavLink to="/contact">{t('nav.contact')}</NavLink></li>
          </ul>

          {/* Actions */}
          <div className="navbar__actions">
            <button
              className="navbar__lang"
              onClick={toggleLang}
              aria-label="Switch language"
              title={t('lang.switch')}
            >
              {t('lang.switch')}
            </button>

            {user ? (
              <div className="navbar__user">
                <Link to="/account" className="navbar__icon-btn" title={t('nav.myAccount')}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                  </svg>
                </Link>
              </div>
            ) : (
              <Link to="/login" className="navbar__icon-btn">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                </svg>
              </Link>
            )}

            <button className="navbar__cart-btn" onClick={openCart}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/>
                <path d="M16 10a4 4 0 01-8 0"/>
              </svg>
              {count > 0 && <span className="navbar__badge">{count}</span>}
            </button>

            <button className="navbar__hamburger" onClick={() => setMenuOpen(!menuOpen)}>
              <span /><span /><span />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="mobile-menu">
          <button className="mobile-menu__close" onClick={() => setMenuOpen(false)}>✕</button>
          <ul>
            <li><Link to="/shop" onClick={() => setMenuOpen(false)}>{t('nav.shopAll')}</Link></li>
            <li><Link to="/shop?category=raw-honey" onClick={() => setMenuOpen(false)}>{t('nav.rawHoney')}</Link></li>
            <li><Link to="/shop?category=honeycomb" onClick={() => setMenuOpen(false)}>{t('nav.infusedHoney')}</Link></li>
            <li><Link to="/about" onClick={() => setMenuOpen(false)}>{t('nav.ourStory')}</Link></li>
            <li><Link to="/contact" onClick={() => setMenuOpen(false)}>{t('nav.contact')}</Link></li>
            <li>
              <button onClick={() => { toggleLang(); setMenuOpen(false); }}>
                {t('lang.switch')}
              </button>
            </li>
            {user ? (
              <><li><Link to="/account" onClick={() => setMenuOpen(false)}>{t('nav.myAccount')}</Link></li>
              <li><button onClick={() => { logout(); setMenuOpen(false); }}>{t('nav.logout')}</button></li></>
            ) : (
              <li><Link to="/login" onClick={() => setMenuOpen(false)}>{t('nav.loginRegister')}</Link></li>
            )}
          </ul>
        </div>
      )}

      <style>{`
        .navbar {
          position: fixed; top: 0; left: 0; right: 0; z-index: 1000;
          transition: all 0.3s ease;
          padding: 16px 0;
          background: var(--gold);
        }
        .navbar--scrolled {
          background: var(--gold);
          box-shadow: 0 2px 20px rgba(44,36,22,0.18);
          padding: 10px 0;
        }
        .navbar__inner { display: flex; align-items: center; justify-content: space-between; }
        .navbar__logo { display: flex; align-items: center; flex-shrink: 0; }
        .navbar__logo img { height: 44px; width: auto; max-width: none; display: block; }
        .navbar__links { display: flex; list-style: none; gap: 32px; }
        .navbar__links a { font-weight: 500; font-size: 0.95rem; color: var(--text); transition: opacity 0.2s; }
        .navbar__links a:hover, .navbar__links a.active { opacity: 0.65; }
        .navbar__actions { display: flex; align-items: center; gap: 16px; }
        .navbar__lang {
          padding: 6px 14px; border: 1.5px solid var(--text); border-radius: 50px;
          background: transparent; color: var(--text); font-size: 0.78rem; font-weight: 700;
          font-family: var(--font-sans); cursor: pointer; transition: all 0.2s; letter-spacing: 0.5px;
          min-width: 64px;
        }
        .navbar__lang:hover { background: var(--text); color: var(--gold); }
        .navbar__icon-btn { color: var(--text); transition: opacity 0.2s; display: flex; }
        .navbar__icon-btn:hover { opacity: 0.65; }
        .navbar__cart-btn { position: relative; color: var(--text); display: flex; transition: opacity 0.2s; }
        .navbar__cart-btn:hover { opacity: 0.65; }
        .navbar__badge {
          position: absolute; top: -8px; right: -8px; background: var(--text); color: var(--gold);
          width: 18px; height: 18px; border-radius: 50%; font-size: 0.65rem; font-weight: 700;
          display: flex; align-items: center; justify-content: center;
        }
        [dir="rtl"] .navbar__badge { right: auto; left: -8px; }
        .navbar__hamburger { display: none; flex-direction: column; gap: 5px; }
        .navbar__hamburger span { width: 24px; height: 2px; background: var(--text); border-radius: 2px; }
        .mobile-menu {
          position: fixed; inset: 0; background: var(--cream); z-index: 2000;
          display: flex; flex-direction: column; padding: 60px 30px;
        }
        .mobile-menu__close {
          position: absolute; top: 20px; right: 20px; font-size: 1.4rem; color: var(--text);
        }
        [dir="rtl"] .mobile-menu__close { right: auto; left: 20px; }
        .mobile-menu ul { list-style: none; display: flex; flex-direction: column; gap: 8px; }
        .mobile-menu ul li a, .mobile-menu ul li button {
          font-family: var(--font-serif); font-size: 1.8rem; font-weight: 500;
          color: var(--text); display: block; padding: 8px 0;
        }
        @media (max-width: 768px) {
          .navbar__links { display: none; }
          .navbar__hamburger { display: flex; }
        }
      `}</style>
    </>
  );
};

export default Navbar;
